/**
 * Helper: Product Inventory
 * -----------------------------------------------------------------------------
 * Methods for fetching product inventory data using a custom app api
 *
 * @namespace productInventory
 */
import axios from 'axios';
import INVENTORY_LEVEL_LOCATIONS from '../consts/inventoryLevelLocations';

function parseInventoryMeta(meta, store) {
  const data = {};
  for (const prop in meta) {
    if (!meta.hasOwnProperty(prop)) continue;
    const split = prop.split('-');
    const variantId = split[0];
    const locationId = split[1];
    if (!data.hasOwnProperty(variantId)) {
      data[variantId] = {};
    }
    const locationName = INVENTORY_LEVEL_LOCATIONS(store).hasOwnProperty(locationId) ? INVENTORY_LEVEL_LOCATIONS(store)[locationId] : locationId;
    data[variantId][locationName] = meta[prop];
  }
  return data;
}

export function fetchProductInventory(productId, productHandle) {
  return new Promise((resolve, reject) => {
    Frame.stockData = Frame.stockData || [];

    /**
     * Use cached stock data if it has already been requested.
     */
    if (typeof Frame.stockData[productId] !== 'undefined') {
      resolve(Frame.stockData[productId]);
      return;
    }

    /**
     * Previously they got all this from an API. But now we handle it internally.
     *
     * Mechanic runs a task which stores location inventory levels as meta against a product.
     * Here we parse that meta data, transforming it to resemble the API response they were previously using.
     * In theory, that'll mean everything works...
     */
    try {
      // get product meta. this is rendered to the page in product.liquid
      const meta = window.productInventoryLocationMeta[productId] ?? false;
      if (!meta) {
        throw 'Meta not found'
      }
      const store = window.theme.store.id ?? 'gb_store'

      // got meta?
      if (Object.keys(meta).length === 0) {
        reject(false);
        return false;
      }
      // The order of the inventory is important - as we want
      // to show the highest ranking stock message from the correct location
      const sortNameArray = Object.values(INVENTORY_LEVEL_LOCATIONS(store));

      // parse meta
      const inventoryMetaParsed = parseInventoryMeta(meta, store);
      const data = [];

      // create data to return
      for (const variant in inventoryMetaParsed) {
        if (!inventoryMetaParsed.hasOwnProperty(variant)) continue
        const item = {
          'id':'gid://shopify/ProductVariant/' + variant,
          'variantId': variant,
          'inventoryQuantity': 0,
          'inventoryItem': {
            'inventoryLevels': {
              'edges': []
            }
          },
        };
        for (const location in inventoryMetaParsed[variant]) {
          if (!inventoryMetaParsed[variant].hasOwnProperty(location)) continue;
          const edge = {
            'node': {
              'available': isNaN(parseInt(inventoryMetaParsed[variant][location])) ? 0 : parseInt(inventoryMetaParsed[variant][location]),
              'sort': sortNameArray.indexOf(location),
              'location': {
                'name': location
              }
            }
          };
          item.inventoryItem.inventoryLevels.edges.push(edge)
          item.inventoryQuantity += edge.node.available;
        }
        data.push(item);
      }

      Frame.stockData[productId] = data;
      resolve(data);
    } catch (e) {
      console.error('failed to retrieve product meta', e);
      reject(false);
    }
  });
}

export function getCurrentVariantStockData(variantId, productData) {
  let currentVariantStockData = productData.find((obj) => {
    return parseInt(obj.variantId, 10) === parseInt(variantId, 10);
  });

  currentVariantStockData = currentVariantStockData.inventoryItem.inventoryLevels.edges;
  currentVariantStockData = currentVariantStockData.map((item) => item.node);
  currentVariantStockData.sort((a, b) => a.sort - b.sort);

  return currentVariantStockData;
}
